import { ArrowLeft, ArrowRight, Button, Select } from "@ews-admin/global-design-system";
import React, { useEffect, useMemo, useRef } from "react";
import { useTranslation } from "react-i18next";

import { scrollToTop } from "../../../utils/scroll.utils";
interface PaginationProps {
  totalItems: number;
  itemsPerPage: number;
  currentPage: number;
  onPageChange: (page: number) => void;
  onItemsPerPageChange: (itemsPerPage: number) => void;
  isLoading?: boolean;
  className?: string;
}

export const Pagination: React.FC<PaginationProps> = ({
  totalItems,
  itemsPerPage,
  currentPage,
  onPageChange,
  onItemsPerPageChange,
  isLoading = false,
  className = "",
}) => {
  const { t } = useTranslation();
  const containerRef = useRef<HTMLDivElement>(null);

  // Navigate from a user click, then bring the page back to its top
  const goToPage = (page: number) => {
    onPageChange(page);
    scrollToTop(containerRef.current);
  };

  // Calculate total pages using useMemo to avoid unnecessary recalculations
  const totalPages = useMemo(() => {
    const pages = Math.ceil(totalItems / itemsPerPage);
    return Math.max(1, pages);
  }, [totalItems, itemsPerPage]);
  // Generate page numbers to display
  const pageNumbers = useMemo(() => {
    const pages = [];
    const maxPagesToShow = 5;

    if (totalPages <= maxPagesToShow) {
      // Show all pages if there are few
      for (let i = 1; i <= totalPages; i++) {
        pages.push(i);
      }
    } else {
      // Always show first page
      pages.push(1);

      // Calculate start and end of page range
      let start = Math.max(2, currentPage - 1);
      let end = Math.min(totalPages - 1, currentPage + 1);

      // Adjust if we're near the beginning
      if (currentPage <= 3) {
        end = Math.min(totalPages - 1, 4);
      }

      // Adjust if we're near the end
      if (currentPage >= totalPages - 2) {
        start = Math.max(2, totalPages - 3);
      }

      // Add ellipsis if needed
      if (start > 2) {
        pages.push("ellipsis-start");
      }

      // Add middle pages
      for (let i = start; i <= end; i++) {
        pages.push(i);
      }

      // Add ellipsis if needed
      if (end < totalPages - 1) {
        pages.push("ellipsis-end");
      }

      // Always show last page
      if (totalPages > 1) {
        pages.push(totalPages);
      }
    }

    return pages;
  }, [totalPages, currentPage]);

  // Ensure current page is valid
  useEffect(() => {
    if (currentPage > totalPages && totalPages > 0) {
      onPageChange(totalPages);
    }
  }, [currentPage, totalPages, onPageChange]);

  // Calculate the range of items being displayed
  const itemRangeStart =
    totalItems === 0 ? 0 : (currentPage - 1) * itemsPerPage + 1;
  const itemRangeEnd = Math.min(currentPage * itemsPerPage, totalItems);

  return (
    <div
      ref={containerRef}
      className={`flex flex-col gap-4 justify-between items-center py-4 md:flex-row ${className}`}
    >
      <div className="flex items-center text-sm text-gray-500">
        {t("pagination.showing")}{" "}
        <span className="mx-1 font-medium">{itemRangeStart}</span>{" "}
        {t("pagination.to")}{" "}
        <span className="mx-1 font-medium">{itemRangeEnd}</span>{" "}
        {t("pagination.of")}{" "}
        <span className="mx-1 font-medium">{totalItems}</span>{" "}
        {t("pagination.results")}
      </div>

      <div className="flex flex-col gap-3 items-center sm:flex-row sm:items-center">
        <div className="flex items-center">
          <Select
            // label={t("pagination.show")}
            value={itemsPerPage}
            onChange={(value) => onItemsPerPageChange(Number(value))}
            disabled={isLoading}
            options={[
              { label: "5", value: 5 },
              { label: "10", value: 10 },
              { label: "25", value: 25 },
              { label: "50", value: 50 },
              { label: "100", value: 100 },
            ]}
          />
        </div>

        {totalPages > 1 && (
          <div className="flex gap-2 items-center">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => goToPage(currentPage - 1)}
              disabled={currentPage === 1 || isLoading}
              className="flex items-center px-3"
            >
              <ArrowLeft className="mr-2 w-4 h-4" />
              {t("pagination.previous")}
            </Button>

            <div className="hidden gap-1 items-center md:flex">
              {pageNumbers.map((page, index) => {
                if (page === "ellipsis-start" || page === "ellipsis-end") {
                  return (
                    <span
                      key={`ellipsis-${index}`}
                      className="px-3 py-1 text-gray-500"
                    >
                      ...
                    </span>
                  );
                }

                return (
                  <Button
                    key={`page-${page}`}
                    variant={currentPage === page ? "ews-primary" : "ghost"}
                    size="sm"
                    onClick={() => goToPage(Number(page))}
                    disabled={isLoading}
                    className="min-w-[32px]"
                  >
                    {page}
                  </Button>
                );
              })}
            </div>

            <div className="flex items-center text-sm md:hidden">
              <span className="px-3 py-1 bg-gray-100 rounded-md">
                {t("pagination.pageOf", {
                  current: currentPage,
                  total: totalPages,
                })}
              </span>
            </div>

            <Button
              variant="ghost"
              size="sm"
              onClick={() => goToPage(currentPage + 1)}
              disabled={currentPage === totalPages || isLoading}
              className="flex items-center px-3"
            >
              {t("pagination.next")}
              <ArrowRight className="ml-2 w-4 h-4" />
            </Button>
          </div>
        )}
      </div>
    </div>
  );
};
